import { Box } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { useEntitiesHook } from "../../hooks/api/useEntitiesHook";
import { useUnderwritingHook } from "../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../redux/actions/loanDrilldownActions";
import AssociationsDisplay from "./AssociationsDisplay";
import AssociationsEditor from "./AssociationsEditor";

// The data model is INVERSE: a loan document does not store its own
// associations; instead each borrower / subject property / entity carries an
// `associations.loanDocuments` array pointing back at the docs it's linked
// to. Saving requires per-entity update calls with delta-applied lists, not
// a single update on the doc.
const DOC_ASSOCIATION_FIELDS = ["borrowers", "subjectProperties", "entities"];

// Build the forward map { borrowers: [ids], subjectProperties: [ids],
// entities: [ids] } from the inverse association data on the loan.
const buildResolvedAssociations = (loan, docId) => {
  const empty = { borrowers: [], subjectProperties: [], entities: [] };
  if (!loan || !docId) return empty;
  const matches = (collection) =>
    (collection || [])
      .filter((item) => item?.associations?.loanDocuments?.includes(docId))
      .map((item) => item._id);
  const entityList = Array.isArray(loan?.entities)
    ? loan.entities
    : loan?.borrowerEntity
      ? [loan.borrowerEntity]
      : [];
  return {
    borrowers: matches(loan.borrowers),
    subjectProperties: matches(loan.subjectProperties),
    entities: matches(entityList),
  };
};

// For a given entity collection, figure out which entities need an update
// call to add/remove docId from their `associations.loanDocuments`.
const planEntityUpdates = (collection, newSelectedIds, docId) => {
  const newSet = new Set(newSelectedIds || []);
  return (collection || [])
    .map((item) => {
      if (!item?._id) return null;
      const had = !!item?.associations?.loanDocuments?.includes(docId);
      const should = newSet.has(item._id);
      if (had === should) return null;
      const currentDocs = item?.associations?.loanDocuments || [];
      const nextDocs = should
        ? [...currentDocs, docId]
        : currentDocs.filter((id) => id !== docId);
      return {
        item,
        nextAssociations: {
          ...(item.associations || {}),
          loanDocuments: nextDocs,
        },
      };
    })
    .filter(Boolean);
};

const DocumentAssociations = ({
  doc,
  loan,
  loanId,
  iconsOnly = false,
  compactEmpty = false,
}) => {
  const dispatch = useDispatch();
  const { updateBorrower, updateSubjectProperty } = useUnderwritingHook();
  const { updateEntityData } = useEntitiesHook();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const resolved = useMemo(
    () => buildResolvedAssociations(loan, doc?._id),
    [loan, doc?._id],
  );

  const handleSave = useCallback(
    async (selected) => {
      if (!doc?._id || !loanId) return;
      const docId = doc._id;
      const borrowerUpdates = planEntityUpdates(
        loan?.borrowers,
        selected?.borrowers,
        docId,
      );
      const propertyUpdates = planEntityUpdates(
        loan?.subjectProperties,
        selected?.subjectProperties,
        docId,
      );
      const entityList = Array.isArray(loan?.entities)
        ? loan.entities
        : loan?.borrowerEntity
          ? [loan.borrowerEntity]
          : [];
      const entityUpdates = planEntityUpdates(
        entityList,
        selected?.entities,
        docId,
      );

      if (
        !borrowerUpdates.length &&
        !propertyUpdates.length &&
        !entityUpdates.length
      ) {
        setEditing(false);
        return;
      }

      setSaving(true);
      const result = { last: null };
      const captureResponse = (d) => {
        if (d) result.last = d;
      };
      try {
        for (const u of borrowerUpdates) {
          await updateBorrower({
            loanId,
            borrowerId: u.item._id,
            data: { associations: u.nextAssociations },
            onSuccessFn: captureResponse,
          });
        }
        for (const u of propertyUpdates) {
          await updateSubjectProperty({
            loanId,
            propertyId: u.item._id,
            data: { associations: u.nextAssociations },
            onSuccessFn: captureResponse,
          });
        }
        for (const u of entityUpdates) {
          await updateEntityData({
            loanId,
            entityId: u.item._id,
            data: { associations: u.nextAssociations },
            onSuccessFn: captureResponse,
          });
        }
        if (result.last) {
          dispatch(loanDrilldownSet(result.last));
        }
        setEditing(false);
      } finally {
        setSaving(false);
      }
    },
    [
      doc?._id,
      loanId,
      loan,
      updateBorrower,
      updateSubjectProperty,
      updateEntityData,
      dispatch,
    ],
  );

  if (!doc?._id) return null;

  const hostLabel = doc?.file_display_name || "Document";

  return (
    <Box>
      <AssociationsDisplay
        loan={loan}
        associations={resolved}
        enabledFields={DOC_ASSOCIATION_FIELDS}
        onEdit={() => setEditing(true)}
        iconsOnly={iconsOnly}
        compactEmpty={compactEmpty}
      />
      <AssociationsEditor
        open={editing}
        loan={loan}
        initial={resolved}
        enabledFields={DOC_ASSOCIATION_FIELDS}
        hostLabel={hostLabel}
        onSave={handleSave}
        onClose={() => !saving && setEditing(false)}
        saving={saving}
      />
    </Box>
  );
};

export default DocumentAssociations;
export { DOC_ASSOCIATION_FIELDS, buildResolvedAssociations, planEntityUpdates };
